import React from 'react';
import { Box, HStack, SimpleGrid, Text, VStack, Icon, IconButton } from '@chakra-ui/react';
import { FaBox, FaCheck, FaStore, FaHandshake } from 'react-icons/fa';
import { FiCamera, FiMapPin, FiMinus, FiPlus, FiVideo } from 'react-icons/fi';
import { CampaignType } from '@foodfluence/shared';
import { campaignTypeDisplayNames } from '../../../types';
import { UGC_RATE_PRESETS, UgcRatePreset } from '../../../utils/contentUtils';
import { ff } from '../../../theme';

export const getPresetTitle = (preset: UgcRatePreset): string =>
  preset.displayName.replace(/\s*\(.*\)$/, '');

export const getPresetSpec = (preset: UgcRatePreset): string => {
  if (preset.type === 'photo') {
    return preset.count === 1 ? '1 photo' : `${preset.count ?? 2}+ photos`;
  }
  const parenthetical = preset.displayName.match(/\(([^)]+)\)/)?.[1];
  return parenthetical ? `${parenthetical} video` : 'video';
};

interface UgcDeliverablesPickerProps {
  counts: Record<string, number>;
  onChange: (counts: Record<string, number>) => void;
  /** Creator's own minimum rate per preset displayName, when they've set one */
  rateByPreset: Record<string, number>;
}

/** Tile grid: an unpicked piece shows "+ Add"; a picked one gets a dark border and a − n + stepper. */
const UgcDeliverablesPicker: React.FC<UgcDeliverablesPickerProps> = ({ counts, onChange, rateByPreset }) => {
  const setCount = (preset: UgcRatePreset, count: number) => {
    onChange({ ...counts, [preset.displayName]: Math.max(0, count) });
  };

  return (
    <SimpleGrid columns={{ base: 2, md: 3 }} spacing="10px" fontFamily="display">
      {UGC_RATE_PRESETS.map((preset) => {
        const count = counts[preset.displayName] || 0;
        const rate = rateByPreset[preset.displayName];
        const title = getPresetTitle(preset);
        return (
          <VStack
            key={preset.displayName}
            align="stretch"
            justify="space-between"
            spacing="10px"
            p="12px"
            bg="white"
            borderRadius="20px"
            border={count > 0 ? `1.5px solid ${ff.ink}` : `1px solid ${ff.line}`}
            transition="border-color 0.15s"
          >
            <HStack spacing="10px" align="start">
              <Box flex="none" w="36px" h="36px" borderRadius="10px" bg={ff.grey} color={ff.ink} display="flex" alignItems="center" justifyContent="center">
                <Icon as={preset.type === 'photo' ? FiCamera : FiVideo} boxSize="18px" aria-hidden />
              </Box>
              <Box minW={0}>
                <Text fontSize="15px" color={ff.ink} fontWeight={600} lineHeight="1.2">{title}</Text>
                <Text fontSize="13px" color={ff.text2} mt="2px" lineHeight="1.3">
                  {getPresetSpec(preset)}
                  {rate !== undefined && ` · from $${rate}`}
                </Text>
              </Box>
            </HStack>
            {count === 0 ? (
              <Box
                as="button"
                type="button"
                aria-label={`Increase ${title}`}
                onClick={() => setCount(preset, 1)}
                h="44px"
                border={`1px dashed ${ff.border}`}
                borderRadius="full"
                fontSize="14px"
                fontWeight={600}
                color={ff.ink}
                _hover={{ bg: ff.grey }}
              >
                + Add
              </Box>
            ) : (
              <HStack justify="space-between">
                <IconButton
                  icon={<FiMinus />}
                  aria-label={`Decrease ${title}`}
                  onClick={() => setCount(preset, count - 1)}
                  w="44px"
                  minW="44px"
                  h="44px"
                  borderRadius="full"
                  border={`1.5px solid ${ff.ink}`}
                  bg="white"
                  color={ff.ink}
                  _hover={{ bg: ff.grey }}
                />
                <Text textStyle="display" fontSize="26px" letterSpacing="0" color={ff.ink}>{count}</Text>
                <IconButton
                  icon={<FiPlus />}
                  aria-label={`Increase ${title}`}
                  onClick={() => setCount(preset, count + 1)}
                  w="44px"
                  minW="44px"
                  h="44px"
                  borderRadius="full"
                  bg={ff.ink}
                  color={ff.lime}
                  _hover={{ bg: ff.inkSoft }}
                />
              </HStack>
            )}
          </VStack>
        );
      })}
    </SimpleGrid>
  );
};

// Creator-facing logistics copy per collab type; titles follow campaignTypeDisplayNames
// (shopify deliberately reads as "Ship to Home" — the storefront is a brand-side detail)
export const LOGISTICS_META: Record<CampaignType, { description: string; icon: React.ElementType }> = {
  inStore: { description: 'Visit their location to shoot your content', icon: FiMapPin },
  shipToHome: { description: 'They ship you product to shoot at home', icon: FaBox },
  shopify: { description: 'They ship you product to shoot at home', icon: FaBox },
  retail: { description: 'Pick up their product from a retail store', icon: FaStore },
  brandManaged: { description: 'Work out logistics directly with the brand', icon: FaHandshake },
};

interface UgcLogisticsPickerProps {
  options: CampaignType[];
  value: CampaignType;
  onChange: (value: CampaignType) => void;
}

export const UgcLogisticsPicker: React.FC<UgcLogisticsPickerProps> = ({ options, value, onChange }) => (
  <SimpleGrid columns={2} spacing={3}>
    {options.map((type) => {
      const option = {
        value: type,
        title: campaignTypeDisplayNames[type],
        ...LOGISTICS_META[type],
      };
      const isSelected = value === option.value;
      return (
        <Box
          key={option.value}
          as="button"
          type="button"
          flex={1}
          position="relative"
          p={4}
          borderRadius="20px"
          textAlign="left"
          border={isSelected ? '1.5px solid' : '1px solid'}
          borderColor={isSelected ? ff.ink : ff.line}
          bg="white"
          transition="border-color 0.15s"
          _hover={{ borderColor: ff.ink }}
          onClick={() => onChange(option.value)}
        >
          {isSelected && (
            <Box
              position="absolute"
              top={2.5}
              right={2.5}
              w="20px"
              h="20px"
              borderRadius="full"
              bg={ff.ink}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaCheck} color={ff.lime} boxSize="10px" />
            </Box>
          )}
          <Box
            display="inline-flex"
            bg={ff.grey}
            p="10px"
            borderRadius="12px"
            color={ff.ink}
            mb={3}
          >
            <Icon as={option.icon} boxSize="16px" />
          </Box>
          <Text fontSize="15px" fontWeight={600} color={ff.ink}>{option.title}</Text>
          <Text fontSize="13px" color={ff.text2} mt={0.5}>{option.description}</Text>
        </Box>
      );
    })}
  </SimpleGrid>
);

export default UgcDeliverablesPicker;
